import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  sourceLabel: {
    id: 'companyProfile.siteSources.sourceLabel',
    defaultMessage: 'Source',
    description: 'Label preceding a link (or comma-separated links) to the company\'s own page(s) this profile text was sourced from',
  },
});

interface CompanySiteSourcesProps {
  company: Pick<AlgoliaCompanyRecord, 'siteSources'>;
  className?: string;
}

/**
 * A compact citation line for the About text, not a full card like
 * CompanyCommunityInsights — every source here is the company's own domain by
 * definition, so there's no need to spell the domain out next to each link.
 */
export function CompanySiteSources({ company, className = '' }: CompanySiteSourcesProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const sources = company.siteSources;
  if (!sources || sources.length === 0) return null;

  return (
    <p className={`mt-1.5 text-xs text-[#7A6E5F] dark:text-[#B8A48C] ${className}`}>
      {formatMessage(messages.sourceLabel)}:{' '}
      {sources.map((source, index) => (
        <React.Fragment key={source.url}>
          {index > 0 && ', '}
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#3B5E45] underline decoration-[#3B5E45]/30 underline-offset-2 hover:text-[#2A4533] dark:text-[#98CFA8] dark:decoration-[#98CFA8]/40 dark:hover:text-[#B6D4BC]"
            title={source.url}
          >
            {source.title || source.domain}
          </a>
        </React.Fragment>
      ))}
    </p>
  );
}

CompanySiteSources.displayName = 'CompanySiteSources';
